import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api, { formatApiError } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { QrCode, Camera, StopCircle, PuzzlePiece, Stack, Wrench, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const READER_ID = "qr-reader-region";

export default function Scan() {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [result, setResult] = useState(null);
  const [defectOpen, setDefectOpen] = useState(false);
  const [defect, setDefect] = useState({ bezeichnung: "", mangelklasse: "", prioritaet: "mittel", sicherheitsrelevant: false, beschreibung: "", fotos: [] });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const token = localStorage.getItem("csp_token");
  const addDefectPhoto = async (files) => {
    setUploadingPhoto(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", "Mangel");
        const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setDefect((d) => ({ ...d, fotos: [...d.fotos, data.id] }));
      }
    } catch (e) { toast.error("Foto-Upload fehlgeschlagen", { description: formatApiError(e.response?.data?.detail) }); }
    setUploadingPhoto(false);
  };
  const [saving, setSaving] = useState(false);

  const submitDefect = async () => {
    if (!defect.bezeichnung.trim() || !result) return;
    setSaving(true);
    try {
      const assetId = result.asset?.id || result.component?.asset_id;
      await api.post(`/v1/assets/${assetId}/defects/manual`, {
        bezeichnung: defect.bezeichnung.trim(),
        beschreibung: defect.beschreibung || null,
        mangelklasse: defect.mangelklasse || null,
        prioritaet: defect.prioritaet,
        sicherheitsrelevant: defect.sicherheitsrelevant,
        fotos: defect.fotos,
        component_id: result.type === "component" ? result.component?.id : null,
      });
      toast.success("Mangel erfasst");
      setDefectOpen(false);
      setDefect({ bezeichnung: "", mangelklasse: "", prioritaet: "mittel", sicherheitsrelevant: false, beschreibung: "", fotos: [] });
    } catch (e) {
      toast.error("Mangel konnte nicht erfasst werden", { description: formatApiError(e.response?.data?.detail) });
    }
    setSaving(false);
  };

  const resolve = async (code) => {
    const c = (code || "").trim();
    if (!c) return;
    try {
      const { data } = await api.get(`/qr/resolve?code=${encodeURIComponent(c)}`);
      setResult(data);
      toast.success("QR-Code aufgelöst", { description: data.type === "component" ? data.component?.bezeichnung : data.asset?.name });
    } catch (e) {
      toast.error("QR-Code nicht gefunden", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const stop = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch (e) { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const start = async () => {
    setResult(null);
    setScanning(true);
    try {
      const scanner = new Html5Qrcode(READER_ID);
      scannerRef.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 250 },
        async (decoded) => { await stop(); resolve(decoded); },
        () => { /* ignore per-frame errors */ });
    } catch (e) {
      setScanning(false);
      toast.error("Kamera nicht verfügbar", { description: "Bitte Code manuell eingeben." });
    }
  };

  useEffect(() => () => { if (scannerRef.current) { scannerRef.current.stop().catch(() => {}); } }, []);

  const targetAssetId = result?.asset?.id || result?.component?.asset_id;

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader title="QR-Scanner" subtitle="Komponente oder Anlage per QR-Code öffnen und Wartung/Mangel starten" />

      <Card className="rounded-md border p-4" data-testid="scan-card">
        <div id={READER_ID} className="mx-auto max-w-sm overflow-hidden rounded-md" data-testid="qr-reader" />
        <div className="mt-3 flex justify-center gap-2">
          {!scanning
            ? <Button className="gap-2" data-testid="scan-start" onClick={start}><Camera size={16} weight="bold" /> Kamera starten</Button>
            : <Button variant="outline" className="gap-2" data-testid="scan-stop" onClick={stop}><StopCircle size={16} weight="bold" /> Stoppen</Button>}
        </div>
        <div className="mt-4">
          <Label2>Oder Code manuell eingeben</Label2>
          <div className="mt-1 flex gap-2">
            <Input data-testid="scan-manual-input" className="font-mono" placeholder="z. B. CSP-CMP-1A2B3C4D"
              value={manual} onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => e.key === "Enter" && resolve(manual)} />
            <Button variant="outline" data-testid="scan-manual-resolve" onClick={() => resolve(manual)} disabled={!manual.trim()}>Öffnen</Button>
          </div>
        </div>
      </Card>

      {result && (
        <Card className="mt-4 rounded-md border p-4" data-testid="scan-result">
          <div className="flex items-center gap-2">
            {result.type === "component" ? <PuzzlePiece size={20} weight="duotone" className="text-primary" /> : <Stack size={20} weight="duotone" className="text-primary" />}
            <div>
              <div className="text-sm font-semibold" data-testid="scan-result-name">
                {result.type === "component" ? result.component?.bezeichnung : result.asset?.name}
              </div>
              <div className="text-xs text-muted-foreground">
                {result.type === "component" ? `Komponente an: ${result.asset?.name || "—"}` : (result.asset?.asset_type || "Anlage")}
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Button variant="outline" className="gap-2" data-testid="scan-open" onClick={() => navigate(`/anlage/${targetAssetId}`)}>
              <QrCode size={15} /> Öffnen
            </Button>
            <Button className="gap-2" data-testid="scan-start-maintenance" onClick={() => navigate("/wartungsauftraege")}>
              <Wrench size={15} /> Wartung starten
            </Button>
            <Button variant="outline" className="gap-2 border-red-500/40 text-red-600" data-testid="scan-report-defect" onClick={() => setDefectOpen(true)}>
              <WarningCircle size={15} /> Mangel melden
            </Button>
          </div>
        </Card>
      )}

      <Dialog open={defectOpen} onOpenChange={setDefectOpen}>
        <DialogContent data-testid="defect-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Mangel melden</DialogTitle></DialogHeader>
          <DialogDescription>
            {result?.type === "component" ? `Komponente: ${result?.component?.bezeichnung}` : `Anlage: ${result?.asset?.name}`}
          </DialogDescription>
          <div className="space-y-3">
            <div><Label2>Bezeichnung *</Label2><Input data-testid="defect-bezeichnung" value={defect.bezeichnung} onChange={(e) => setDefect({ ...defect, bezeichnung: e.target.value })} placeholder="Kurzbeschreibung des Mangels" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label2>Mangelklasse</Label2>
                <Select value={defect.mangelklasse || "none"} onValueChange={(v) => setDefect({ ...defect, mangelklasse: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="defect-mangelklasse"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="geringfuegig">Geringfügig</SelectItem><SelectItem value="erheblich">Erheblich</SelectItem><SelectItem value="gefaehrlich">Gefährlich</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label2>Priorität</Label2>
                <Select value={defect.prioritaet} onValueChange={(v) => setDefect({ ...defect, prioritaet: v })}>
                  <SelectTrigger data-testid="defect-prioritaet"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="niedrig">Niedrig</SelectItem><SelectItem value="mittel">Mittel</SelectItem><SelectItem value="hoch">Hoch</SelectItem><SelectItem value="kritisch">Kritisch</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label2>Beschreibung</Label2><Textarea data-testid="defect-beschreibung" value={defect.beschreibung} onChange={(e) => setDefect({ ...defect, beschreibung: e.target.value })} rows={3} /></div>
            <div>
              <Label2>Fotos</Label2>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {defect.fotos.map((mid) => <AuthImage key={mid} mediaId={mid} alt="Mangelfoto" className="h-14 w-14 rounded-md border border-border object-cover" />)}
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent" data-testid="defect-photo-label">
                  <Camera size={15} /> {uploadingPhoto ? "…" : "Foto"}
                  <input type="file" accept="image/*" multiple capture="environment" className="hidden" data-testid="defect-photo-input" onChange={(e) => addDefectPhoto(e.target.files)} />
                </label>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2"><span className="text-sm">Sicherheitsrelevant</span><Switch data-testid="defect-sicherheitsrelevant" checked={defect.sicherheitsrelevant} onCheckedChange={(v) => setDefect({ ...defect, sicherheitsrelevant: v })} /></div>
          </div>
          <DialogFooter><Button data-testid="defect-submit" onClick={submitDefect} disabled={!defect.bezeichnung.trim() || saving}>{saving ? "…" : "Mangel erfassen"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
